import { useEffect, useMemo, useState } from 'react';
import { managerApi } from '../../lib/api';
import { fmtDateBR, fmtHM } from '../../lib/format';
import { urlToDataUrl } from '../../lib/photo';
import { generateReceiptPDF, generateDayReceiptPDF, generateDayReceiptPDFsZip, type DayGroup } from '../../lib/pdf';
import { labelOf } from '../../lib/constants';
import { downloadCSV } from '../../lib/csv';

function exportStamp(): string {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}${mm}_${hh}${mi}`;
}
function slug(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function PontoLogTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [tolerancia, setTolerancia] = useState<number | null>(null);
  const [camarotes, setCamarotes] = useState<string[]>([]);
  const [camarote, setCamarote] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([managerApi.getPontoLog(), managerApi.getConfig()]).then(([log, cfg]) => {
      setRows(log.rows.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()));
      setTolerancia(cfg.config.toleranciaMin);
      setCamarotes(cfg.config.camarotes);
    }).finally(() => setLoading(false));
  }, []);

  const filteredRows = useMemo(() => camarote === 'todos' ? rows : rows.filter((r) => r.camarote === camarote), [rows, camarote]);

  async function downloadPDF(row: any) {
    const foto = row.fotoUrl ? await urlToDataUrl(row.fotoUrl) : null;
    generateReceiptPDF(`Comprovante de Ponto — ${labelOf(row.tipo)}`, { nome: row.nome, cpf: row.cpf, funcao: row.funcao, camarote: row.camarote }, {
      Data: fmtDateBR(row.date), Hora: fmtHM(row.time), Localização: `${row.lat}, ${row.lng}`,
      ...(row.foraDoEsperado ? { 'Fora do esperado': row.justificativa || '—' } : {}),
      'Verificação em cadeia': (row.hashAnterior || '').slice(0, 20) + '…',
    }, foto, row.hash);
  }

  async function buildDayGroups(): Promise<DayGroup[]> {
    const profiles = (await managerApi.getProfiles()).profiles;
    const sigMap = new Map(profiles.map((p: any) => [p.id, p.signatureUrl]));
    const byPerson = new Map<string, DayGroup>();
    for (const r of filteredRows) {
      const key = `${r.employeeId}|${r.date}`;
      if (!byPerson.has(key)) {
        byPerson.set(key, {
          employeeId: r.employeeId,
          emp: { nome: r.nome, cpf: r.cpf, funcao: r.funcao, camarote: r.camarote },
          date: r.date,
          punches: [],
          signatureImg: null,
        });
      }
      const fotoImg = r.fotoUrl ? await urlToDataUrl(r.fotoUrl) : null;
      byPerson.get(key)!.punches.push({ tipo: r.tipo, time: r.time, lat: r.lat, lng: r.lng, foraDoEsperado: r.foraDoEsperado, justificativa: r.justificativa, fotoImg });
    }
    const groups = [...byPerson.values()];
    const sigCache = new Map<string, string | null>();
    for (const g of groups) {
      if (!sigCache.has(g.employeeId)) {
        const url = sigMap.get(g.employeeId);
        sigCache.set(g.employeeId, url ? await urlToDataUrl(url) : null);
      }
      g.signatureImg = sigCache.get(g.employeeId)!;
    }
    groups.sort((a, b) => (a.emp.nome + a.date).localeCompare(b.emp.nome + b.date));
    return groups;
  }

  async function exportUnified() {
    if (filteredRows.length === 0) { alert('Nenhum registro para exportar.'); return; }
    setExporting(true);
    try {
      const groups = await buildDayGroups();
      const nomeArquivo = `pontounificado_${slug(camarote === 'todos' ? 'todos' : camarote)}_${new Set(groups.map((g) => g.employeeId)).size}funcionarios_${exportStamp()}.pdf`;
      await generateDayReceiptPDF(groups, nomeArquivo);
    } finally {
      setExporting(false);
    }
  }

  async function exportIndividual() {
    if (filteredRows.length === 0) { alert('Nenhum registro para exportar.'); return; }
    setExporting(true);
    try {
      const groups = await buildDayGroups();
      const nomeArquivo = `pontoindividual_${slug(camarote === 'todos' ? 'todos' : camarote)}_${new Set(groups.map((g) => g.employeeId)).size}funcionarios_${exportStamp()}.zip`;
      await generateDayReceiptPDFsZip(groups, nomeArquivo);
    } finally {
      setExporting(false);
    }
  }

  function exportCSV() {
    const lines = ['Data,Funcionario,Camarote,Marcacao,Hora,Latitude,Longitude,ForaDoEsperado,Justificativa'];
    filteredRows.forEach((r) => lines.push(`${r.date},${r.nome},${r.camarote},${labelOf(r.tipo)},${fmtHM(r.time)},${r.lat},${r.lng},${r.foraDoEsperado ? 'sim' : 'nao'},"${(r.justificativa || '').replace(/"/g, '""')}"`));
    downloadCSV(lines.join('\n'), 'pontos.csv');
  }

  if (loading) return <div className="card"><p className="muted">Carregando…</p></div>;

  return (
    <div className="card">
      <div className="toolbar"><h2 style={{ margin: 0 }}>Registros de ponto</h2></div>
      <p className="hint">Tolerância aplicada no momento do registro: {tolerancia} min. Registros fora do esperado incluem a justificativa do funcionário. Cada registro tem selfie e hash de verificação — use o botão PDF para o comprovante completo.</p>
      <label>Camarote</label>
      <select value={camarote} onChange={(e) => setCamarote(e.target.value)}>
        <option value="todos">Todos os camarotes</option>
        {camarotes.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn btn-primary btn-sm" style={{ flex: 1 }} disabled={exporting} onClick={exportIndividual}>Exportar por funcionário (.zip)</button>
        <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} disabled={exporting} onClick={exportUnified}>Exportar tudo (PDF único)</button>
        <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={exportCSV}>Exportar CSV</button>
      </div>
      <div className="table-wrap" style={{ marginTop: 14 }}>
        <table>
          <thead><tr><th>Data</th><th>Funcionário</th><th>Camarote</th><th>Marcação</th><th>Hora</th><th>Local</th><th>Status</th><th>Comprovante</th></tr></thead>
          <tbody>
            {filteredRows.length === 0 && <tr><td colSpan={8} className="muted">Nenhum registro ainda.</td></tr>}
            {filteredRows.map((r, i) => (
              <tr key={i}>
                <td>{fmtDateBR(r.date)}</td>
                <td>{r.nome}</td>
                <td>{r.camarote}</td>
                <td>{labelOf(r.tipo)}</td>
                <td className="mono">{fmtHM(r.time)}</td>
                <td>{r.lat ? <a className="maplink" target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${r.lat},${r.lng}`}>ver mapa</a> : '—'}</td>
                <td>{r.foraDoEsperado ? <span className="pill pill-warn" title={r.justificativa}>⚠ fora do esperado</span> : <span className="pill pill-ok">no horário</span>}</td>
                <td><button className="btn btn-ghost btn-sm" onClick={() => downloadPDF(r)}>PDF</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
